import { useEffect, useRef } from "react";
import { H3 } from "../headers";
import { Button } from "../ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";
import { useCountdown } from "usehooks-ts";
import { useForm } from "react-hook-form";
import { FormField } from "../ui/form";
import ehjzleeInstance from "@/lib/ehjzleeInstance";
import config from "@/lib/config";
import { toast } from "sonner";
import { useDispatch } from "react-redux";
import { login } from "@/redux/slices/login.slice";
import { AppDispatch } from "@/redux/store";
import { DrawerTitle } from "../ui/drawer";
import { useTranslations } from "next-intl";
import CircularProgress from "../CircularProgress";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const OTPStep = ({
  onAfterSubmit,
  data,
  onGoBack,
}: {
  onAfterSubmit: (exist: boolean, otp: string) => void;
  data: LoginDataType;
  onGoBack?: () => void;
}) => {
  const t = useTranslations();

  const dispatch: AppDispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const [count, { startCountdown, resetCountdown }] = useCountdown({
    countStart: 60,
    intervalMs: 1000,
  });

  useEffect(() => {
    startCountdown();
    return () => {
      resetCountdown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const { control, handleSubmit, formState } = useForm<OTPDataType>({
    mode: "all",
    defaultValues: {
      otp: "",
    },
    resolver: yupResolver(
      yup.object({
        otp: yup.string().required(),
      })
    ),
  });

  const handleResendOTP = async () => {
    try {
      await ehjzleeInstance.post(config.generate_otp, data);
      resetCountdown();
      startCountdown();
      toast.success(t("otpStepSection.sentToMobile"));
    } catch (err) {
      console.log(err);
      toast.error(t("otpStepSection.failedOTP"));
    }
  };

  const onSubmit = async (values: OTPDataType) => {
    try {
      const res = await ehjzleeInstance.post<{ exist: boolean }>(
        config.verify_otp,
        { ...data, ...values }
      );
      if (res.data.exist) {
        const loginRes = await ehjzleeInstance.post<{
          accessToken: string;
          refreshToken: string;
          firebaseToken: string;
        }>(config.login, { ...data, ...values });
        dispatch(login(loginRes.data));
      }
      onAfterSubmit(res.data.exist, values.otp);
    } catch (err) {
      toast.error(t("otpStepSection.invalidOTP"));
      console.log(err);
    }
  };

  return (
    <>
      <DrawerTitle>
        {t("otpStepSection.drawerTitle")}{" "}
        <span dir="ltr">({data?.mobile})</span>
      </DrawerTitle>
      <H3 className="max-sm:text-center">{t("otpStepSection.enterOtp")}</H3>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormField
          name="otp"
          control={control}
          render={({ field }) => (
            <InputOTP maxLength={4} {...field}>
              <InputOTPGroup dir="ltr" className="mt-14 max-sm:mt-6">
                <InputOTPSlot index={0} ref={inputRef} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
              </InputOTPGroup>
            </InputOTP>
          )}
        />

        <div className="flex items-center justify-between my-4 sm:my-10 ">
          <p className="flex items-center gap-2 flex-wrap sm:gap-4 *:max-sm:text-xs">
            <span>{t("otpStepSection.otpInfo")} </span>
            <Button
              variant={"link"}
              type="button"
              disabled={count > 0}
              onClick={handleResendOTP}
              className="max-sm:p-0 text-green-main disabled:text-gray-light hover:text-green-400 underline underline-offset-2"
            >
              {t("otpStepSection.ButtonOtp")}{" "}
            </Button>
          </p>
          <span className="max-sm:text-xs text-gray-light">
            00:{count.toLocaleString("en-US", { minimumIntegerDigits: 2 })}
          </span>
        </div>
        <Button
          type="submit"
          disabled={!formState.isValid || formState.isSubmitting}
          className="w-full"
        >
          {formState.isSubmitting ? (
            <CircularProgress />
          ) : (
            t("otpStepSection.verificationButton")
          )}
        </Button>
      </form>
      <div className="max-sm:text-xs flex items-center gap-1 justify-center">
        <span>{t("otpStepSection.changeNumber")}</span>
        <Button
          type="button"
          variant={"link"}
          className=" p-0 text-green-main hover:text-green-400 underline underline-offset-2"
          onClick={onGoBack}
        >
          {t("otpStepSection.changeNumberButton")}{" "}
        </Button>
      </div>
    </>
  );
};

export default OTPStep;
